import SectionWrapper from './SectionWrapper';
import { Link, Flex, Grid, Text, Box, Center, Radio, RadioGroup } from '@chakra-ui/react';
import ImageUploadField from './ImageUploadField';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';
import ColorPickerField from './ColorPickerField';
import { useAppSelector, useAppDispatch } from '../../../store/redux/hooks';
import { RootState } from '../../../store/redux/store';
import WatermarkFields from './WatermarkFields';
import { IMAGE_CONFIG } from './utils/imageVisibilityConfig';
import { RADIO_GROUP_SX } from './utils/radioGroupStyles';
import { useEffect, useMemo } from 'react';
import { setWatermarkProp, setSimpleField } from '../../../store/redux/slices/template.slice';
import { ImageField } from '../../../store/types/template';
import { isImageRequired, isImageVisible } from './utils/imageHelpers';
import { useTemplateFieldValidation } from './validation/useTemplateFieldValidation';
import {
  BUILDER_DEVICE_TAB,
  BuilderDeviceTab,
} from './utils/templateBuilderTabs';
import { useEnhancedDesignForTemplate } from './hooks/useEnhancedDesignForTemplate';
import { designRequiresTemplateNfc } from './utils/enhancedDesignEligibility';
import { ENHANCED_EVENT_TICKET_IMAGE_LABELS } from './utils/enhancedEventTicketLabels';
import { APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE } from './utils/appleEnhancedDesignConstants';
import { useDemoPass } from './DemoPassContext';
import { POSTER_DEMO_IMAGES } from './previews/apple/demoPass/posterDemoAssets';

const Design = ({
  selectedTab,
  setSelectedTab,
  watermarkEnabled,
  expressivePassEnabled,
}: {
  selectedTab: BuilderDeviceTab;
  setSelectedTab: (tab: BuilderDeviceTab) => void;
  watermarkEnabled: boolean;
  expressivePassEnabled?: boolean;
}) => {
  const templateType = useAppSelector((s) => s.template.templateType);
  const platform =
    selectedTab === BUILDER_DEVICE_TAB.SHARED
      ? 'shared'
      : selectedTab === BUILDER_DEVICE_TAB.APPLE
        ? 'apple'
        : 'google';

  const passBackgroundColor = useAppSelector(
    (state: RootState) => state.template.colors.passBackgroundColor
  );
  const titleFontColor = useAppSelector(
    (state: RootState) => state.template.colors.titleFontColor
  );
  const bodyFontColor = useAppSelector(
    (state: RootState) => state.template.colors.bodyFontColor
  );
  const stampFeedbackTypes = useAppSelector(
    (s) => s.template.stampFeedbackTypes
  );
  const sectionSubtitle = useMemo(() => {
    if (platform !== 'shared') {
      const platformName = platform === 'apple' ? 'Apple' : 'Google';
      return (
        <Text>
          {`Changes here affect ${platformName} only. `}
          <Link
            as="button"
            type="button"
            color="linkBlue"
            onClick={() => setSelectedTab(BUILDER_DEVICE_TAB.SHARED)}
          >
            Edit in shared →
          </Link>
        </Text>
      );
    }

    if (templateType === 'stampCard') {
      return (
        <Text>
          Read the{' '}
          <Link
            color="linkBlue"
            href="https://docs.passentry.com/documentation/passes/design/guide"
            target="_blank"
          >
            guide
          </Link>{' '}
          on how to create a stamp card pass.
        </Text>
      );
    } else {
      return (
        <Text>
          View the{' '}
          <Link
            color="linkBlue"
            href="https://docs.passentry.com/documentation/passes/design/guide"
            target="_blank"
          >
            design guide
          </Link>{' '}
          for tips and image requirements.
        </Text>
      );
    }
  }, [platform, setSelectedTab, templateType]);

  const dispatch = useAppDispatch();
  const visibleFields = Object.keys(
    IMAGE_CONFIG[templateType]?.[platform] || {}
  ).filter((field: ImageField) =>
    isImageVisible(templateType, platform, field)
  );
  const images = useAppSelector((s) => s.template.images);
  const { validateSection, validateField, handleFocus } =
    useTemplateFieldValidation('images');
  const errors = useAppSelector((s) => s.templateErrors);

  const { templateNfcEnabled, storedDesign } = useEnhancedDesignForTemplate();
  const { isDemoPass } = useDemoPass();
  const needsTemplateNfc =
    designRequiresTemplateNfc(storedDesign) && !templateNfcEnabled;
  const {
    validateField: validateArtworkField,
    validateSection: validateArtworkSection,
  } = useTemplateFieldValidation(APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE);

  const isGoogleTab = platform === 'google';
  const chosenGoogleLogo = useAppSelector((s) => s.template.chosenGoogleLogo);
  const setChosenGoogleLogo = (val: 'icon' | 'wide_logo') => {
    dispatch(setSimpleField({ field: 'chosenGoogleLogo', value: val }));
  };

  const hasImageDimensionErrors = useMemo(
    () =>
      Object.entries(errors).some(([key, message]) => {
        if (!message) return false;
        if (
          key.startsWith(`images.${platform}.`) &&
          key.endsWith('.dimensions')
        ) {
          return true;
        }
        return (
          platform === 'apple' &&
          key === `${APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE}.artwork.dimensions`
        );
      }),
    [errors, platform]
  );

  const bannersSectionOwnsWatermark =
    templateType === 'stampCard' &&
    platform === 'shared' &&
    !!stampFeedbackTypes?.includes('changePassBanner');

  useEffect(() => {
    if (bannersSectionOwnsWatermark) return;

    const hasBanner =
      (platform === 'apple' && images.apple.banner) ||
      (platform === 'google' && images.google.banner) ||
      (platform === 'shared' && images.shared.banner);

    if (!watermarkEnabled || !visibleFields.includes('banner') || !hasBanner) {
      dispatch(
        setWatermarkProp({
          platform: platform,
          field: 'watermarkColor',
          value: '',
        })
      );
      dispatch(
        setWatermarkProp({
          platform: platform,
          field: 'watermarkPosition',
          value: '',
        })
      );
    } else if (
      hasBanner &&
      watermarkEnabled &&
      visibleFields.includes('banner')
    ) {
      const currentWatermarkColor = images[platform].watermarkColor;
      const currentWatermarkPosition = images[platform].watermarkPosition;

      if (!currentWatermarkColor) {
        dispatch(
          setWatermarkProp({
            platform: platform,
            field: 'watermarkColor',
            value: 'light',
          })
        );
      }
      if (!currentWatermarkPosition) {
        dispatch(
          setWatermarkProp({
            platform: platform,
            field: 'watermarkPosition',
            value: 'bottomRight',
          })
        );
      }
    }
  }, [
    watermarkEnabled,
    visibleFields,
    platform,
    images,
    dispatch,
    bannersSectionOwnsWatermark,
  ]);

  return (
    <SectionWrapper
      id="design-section"
      title="Design"
      openOnLoad={true}
      subtitle={sectionSubtitle}
    >
      <Flex direction="column" gap={10}>
        <Flex direction="column" gap={6}>
          {isGoogleTab && visibleFields.includes('wideLogo') && (
            <Text fontSize="15px" mt={2} mb={-2}>
              Choose which is displayed in the centre of your Google Wallet pass:
            </Text>
          )}
          <Grid
            templateColumns={{ base: 'repeat(1, 1fr)', xl: 'repeat(2, 1fr)' }}
            columnGap={8}
            rowGap={3}
          >
            {isGoogleTab && visibleFields.includes('wideLogo') ? (
              <RadioGroup
                value={chosenGoogleLogo}
                onChange={(val) => {
                  const v = val as 'icon' | 'wide_logo';
                  setChosenGoogleLogo(v);
                  handleFocus(
                    v === 'wide_logo'
                      ? 'design.google.wideLogo'
                      : 'design.google.logo'
                  );
                }}
                display="contents"
                sx={{
                  ...RADIO_GROUP_SX,
                  '.chakra-radio__label': {
                    ...RADIO_GROUP_SX['.chakra-radio__label'],
                    ml: '10px',
                  },
                }}
              >
                <Box>
                  <Radio value="wide_logo" mt={1} mb={1} alignItems="center">
                    <Text fontSize="16px" fontWeight="500">Logo</Text>
                  </Radio>
                  <ImageUploadField
                    label=""
                    field="wideLogo"
                    platform="google"
                    errors={errors}
                    validateSection={validateSection}
                    validateField={validateField}
                  />
                </Box>
                <Box>
                  <Radio value="icon" mt={1} mb={1} alignItems="center">
                    <Text fontSize="16px" fontWeight="500">Icon</Text>
                  </Radio>
                  <ImageUploadField
                    label=""
                    field="logo"
                    size="compact"
                    platform="google"
                    errors={errors}
                    validateSection={validateSection}
                    validateField={validateField}
                  />
                </Box>
              </RadioGroup>
            ) : (
              <>
                {visibleFields.includes('logo') && (
                  <ImageUploadField
                    label="Logo"
                    field="logo"
                    platform={platform}
                    isRequired={isImageRequired(
                      templateType,
                      platform,
                      'logo',
                      images
                    )}
                    errors={errors}
                    validateSection={validateSection}
                    validateField={validateField}
                  />
                )}
                {visibleFields.includes('icon') && (
                  <ImageUploadField
                    label="Icon"
                    field="icon"
                    size="compact"
                    platform={platform}
                    isRequired={isImageRequired(
                      templateType,
                      platform,
                      'icon',
                      images
                    )}
                    errors={errors}
                    validateSection={validateSection}
                    validateField={validateField}
                  />
                )}
              </>
            )}
            {visibleFields.includes('banner') &&
              !stampFeedbackTypes?.includes('changePassBanner') &&
              !(
                templateType === 'stampCard' &&
                (platform === 'apple' || platform === 'google')
              ) && (
                <ImageUploadField
                  label="Banner"
                  field="banner"
                  platform={platform}
                  isRequired={isImageRequired(
                    templateType,
                    platform,
                    'banner',
                    images
                  )}
                  expressivePassEnabled={
                    platform === 'google' ? expressivePassEnabled : undefined
                  }
                  errors={errors}
                  validateSection={validateSection}
                  validateField={validateField}
                />
              )}
            {visibleFields.includes('thumbnail') && (
              <ImageUploadField
                label="Thumbnail"
                field="thumbnail"
                platform={platform}
                isRequired={isImageRequired(
                  templateType,
                  platform,
                  'thumbnail',
                  images
                )}
                errors={errors}
                validateSection={validateSection}
                validateField={validateField}
              />
            )}
            {platform === 'apple' && storedDesign !== null && (
              <ImageUploadField
                source="enhancedEventTicket"
                field="artwork"
                label={ENHANCED_EVENT_TICKET_IMAGE_LABELS.artwork}
                isRequired
                isDisabled={needsTemplateNfc || isDemoPass}
                demoPreview={
                  isDemoPass ? POSTER_DEMO_IMAGES.artwork : undefined
                }
                errors={errors}
                validateSection={validateArtworkSection}
                validateField={validateArtworkField}
              />
            )}
          </Grid>
          {hasImageDimensionErrors && (
            <Box bg="#FCF0E7" w="full" py={3} px={4} borderRadius="6px">
              <Flex direction="row" gap={3} alignItems="center">
                <Center>
                  <CustomErrorIcon boxSize="18px" color="#EC6933" />
                </Center>
                <Flex direction="column">
                  <Text textStyle="bodyFamilySemiBold" fontSize="12px">
                    Image size requirements have changed.
                  </Text>
                  <Text textStyle="bodyFamilyRegular" fontSize="12px">
                    You may need to upload new images for this template.
                  </Text>
                </Flex>
              </Flex>
            </Box>
          )}
        </Flex>
        {platform === 'shared' && (
          <Grid
            templateColumns="repeat(2, 1fr)"
            columnGap={8}
            rowGap={3}
            mt={hasImageDimensionErrors ? -6 : 0}
          >
            <ColorPickerField
              label="Background colour"
              color={passBackgroundColor}
              field="passBackgroundColor"
            />
            <ColorPickerField
              label="Title font colour"
              color={titleFontColor}
              field="titleFontColor"
              tooltip="Colour of the field labels. Only applies to Apple Wallet passes."
            />
            <ColorPickerField
              label="Body font colour"
              color={bodyFontColor}
              field="bodyFontColor"
              tooltip="Colour of the field values. Only applies to Apple Wallet passes."
            />
          </Grid>
        )}
        {watermarkEnabled &&
          !stampFeedbackTypes?.includes('changePassBanner') &&
          visibleFields.includes('banner') &&
          ((platform === 'apple' && images.apple.banner) ||
            (platform === 'google' && images.google.banner) ||
            (platform === 'shared' && images.shared.banner)) && (
            <WatermarkFields platform={platform} />
          )}
      </Flex>
    </SectionWrapper>
  );
};

export default Design;
